<script setup lang="ts">
import { ref, onUnmounted, computed, watch, nextTick, defineExpose } from 'vue'
import { useHead } from 'nuxt/app'
import { useApiFetch } from '../composables/useApiFetch'
import { usePopupModal } from '../composables/usePopupModal'
import emblaCarouselVue from 'embla-carousel-vue'
import CloseIcon from '~/assets/svg/close.svg?component'
import ImagePlaceholderIcon from '~/assets/svg/image-placeholder.svg?component'
import ChevronLeftIcon from '~/assets/svg/chevron-left.svg?component'
import ChevronRightIcon from '~/assets/svg/chevron-right.svg?component'

interface Popup {
  id: number
  title: string
  content: string
  link: string
  link_text: string
  is_active: boolean
  start_date: string
  end_date: string
  view_count: number
  click_count: number
  created_at: string
  updated_at: string
  media: string[]
  media_url: string[]
}


const showModal = ref(false)
const currentSlideIndex = ref(0)
const progress = ref(0)
const progressRafId = ref<number | null>(null)

const popups = ref<Popup[]>([])
const [emblaRef, emblaApi] = emblaCarouselVue({ loop: true })

const currentPopup = computed(() => {
  return popups.value[currentSlideIndex?.value] || {}
})

const getShortContent = (content : string) => {
  return content?.length > 180 ? content.slice(0, 180) + '...' : content
}

const {
  currentImageIndex,
  videoElement,
  currentSlideDurationMs,
  currentImageUrl,
  recalculateCurrentSlideDuration,
  playCurrentVideo,
  startImageSlideshow,
  stopImageSlideshow,
  nextImage,
  onVideoLoaded,
  onVideoCanPlay,
  onVolumeChange,
  onVideoPlaying,
  onVideoSeeking,
  onVideoSeeked,
  computeProgressMs,
  initUserPreference,
  resetMediaForNewSlide,
  getMediaType,
} = usePopupModal(currentPopup)

watch(currentSlideIndex, async () => {
  resetMediaForNewSlide()
  startImageSlideshow(() => nextSlide())
  await nextTick()
  setTimeout(() => { playCurrentVideo() }, 200)
})

watch(currentImageIndex, async () => {
  await nextTick()
  setTimeout(() => {
    playCurrentVideo()
  }, 200)
})

watch([currentSlideIndex, currentImageUrl], () => {
  if (getMediaType(currentImageUrl.value) === 'image' && currentImageUrl.value) {
    useHead({
      link: [
        { rel: 'preload', as: 'image', href: currentImageUrl.value, fetchpriority: 'high' },
      ],
    })
  }
})

const fetchActivePopups = async () => {
  try {
    const { data: popupData } = await useApiFetch<Popup[]>('/popup/active')
    popups.value = popupData.value || []
    if (popups.value.length > 0) {
      showModal.value = true
      recalculateCurrentSlideDuration()
      startSlideshow()
      startImageSlideshow(() => nextSlide())
      await incrementViewCount(popups.value[0]?.id)
      playCurrentVideo()
    }
  } catch (_) {}
}

const startSlideshow = () => {
  stopSlideshow()
  progress.value = 0
  const tick = () => {
    progress.value = computeProgressMs()
    if (progress.value >= currentSlideDurationMs.value) {
      if (currentSlideIndex.value >= popups.value.length - 1) {
        closeModal()
        return
      }
      nextSlide()
      return
    }
    progressRafId.value = requestAnimationFrame(tick)
  }
  progressRafId.value = requestAnimationFrame(tick)
}

const stopSlideshow = () => {
  if (progressRafId.value !== null) {
    cancelAnimationFrame(progressRafId.value)
    progressRafId.value = null
  }
}

  const nextSlide = async () => {
    const api = emblaApi.value
    if (api) {
      try { api.scrollNext() } catch (_) {}
      return
    }
    currentSlideIndex.value = (currentSlideIndex.value + 1) % popups.value.length
    resetSlideTimer()
    await nextTick()
    setTimeout(() => {
      playCurrentVideo()
    }, 200)
  }

  const goToSlide = async (index: number) => {
    const api = emblaApi.value
    if (api) {
      try { api.scrollTo(index) } catch (_) {}
      return
    }
    currentSlideIndex.value = index
    resetSlideTimer()
    await nextTick()
    setTimeout(() => {
      playCurrentVideo()
    }, 200)
  }

  const resetSlideTimer = () => {
    progress.value = 0
    resetMediaForNewSlide()
    startSlideshow()
    startImageSlideshow(() => nextSlide())
    incrementViewCount(currentPopup.value.id)
  }

const closeModal = () => {
  showModal.value = false
  stopSlideshow()
  stopImageSlideshow()
}

const incrementViewCount = async (id: number) => {
  if (!id) return
  try {
    await useApiFetch(`/popup/${id}/view`, 'POST')
  } catch (_) {}
}

const incrementClickCount = async () => {
  if (!currentPopup.value.id) return
  try {
    await useApiFetch(`/popup/${currentPopup.value.id}/click`, 'POST')
  } catch (_) {}
}

const onEmblaSelect = async () => {
  const api = emblaApi.value
  if (!api) return
  const selected = api.selectedScrollSnap()
  if (selected !== currentSlideIndex.value) {
    currentSlideIndex.value = selected
    resetSlideTimer()
    await nextTick()
    setTimeout(() => {
      playCurrentVideo()
    }, 200)
  }
}

watch(emblaApi, (api) => {
  if (api) {
    try { api.reInit({ loop: true }) } catch (_) {}
    try { api.off('select', onEmblaSelect) } catch (_) {}
    api.on('select', onEmblaSelect)
  }
})

watch(popups, (list) => {
  const api = emblaApi.value
  if (api) {
    try { api.reInit({ loop: (list?.length || 0) > 1 }) } catch (_) {}
    try { api.scrollTo(currentSlideIndex.value, true) } catch (_) {}
  }
})

// Dışarıdan kontrol için expose edilen API
const loadAndShow = async () => {
  try { initUserPreference() } catch (_) {}
  await fetchActivePopups()
}

const showWithData = async (data: Popup[]) => {
  try { initUserPreference() } catch (_) {}
  popups.value = data || []
  if (popups.value.length > 0) {
    showModal.value = true
    recalculateCurrentSlideDuration()
    startSlideshow()
    startImageSlideshow(() => nextSlide())
    await incrementViewCount(popups.value[0]?.id)
    playCurrentVideo()
  }
}

defineExpose({ loadAndShow, closeModal, showWithData })

onUnmounted(() => {
  stopSlideshow()
  stopImageSlideshow()
})

</script>

<template>
  <Transition 
    enter-active-class="transition-all duration-300 ease-[cubic-bezier(0.52,0.02,0.19,1.02)]"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-all duration-300 ease-[cubic-bezier(0.52,0.02,0.19,1.02)]"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showModal && popups.length > 0"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4"
      @click="closeModal"
    >
      <div
        class="popup-modal flex flex-col"
        @click.stop
      >
        <button
          @click="closeModal"
          class="absolute top-3 right-3 z-10 w-6 h-6 bg-gray-100 text-gray-600
                 hover:text-gray-800 hover:bg-gray-300 rounded-full
                 flex items-center justify-center transition-all duration-200 shadow-lg
                 transform scale-100 hover:scale-105 opacity-50 hover:opacity-100"
          aria-label="Modalı kapat"
        >
          <CloseIcon class="w-6 h-6" />
        </button>
        <div class="overflow-hidden flex-1" ref="emblaRef">
          <div class="flex h-full" :style="!emblaApi ? { transform: `translate3d(-${currentSlideIndex * 100}%, 0, 0)` } : null">
            <div v-for="(popup, index) in popups" :key="popup.id" class="flex-[0_0_100%] min-w-0 flex flex-col h-full">
              <div class="w-full h-[45vh] max-h-[315px] min-h-[200px]
                          sm:h-[40vh] md:h-[315px]
                          flex-shrink-0 flex items-center justify-center">
                <Transition
                  enter-active-class="transition-opacity duration-400 ease-in-out"
                  enter-from-class="opacity-0"
                  enter-to-class="opacity-100"
                  leave-active-class="transition-opacity duration-400 ease-in-out"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                  mode="out-in"
                >
                  <div :key="`${index === currentSlideIndex ? currentPopup?.id : popup.id}-${currentImageIndex}`" class="w-full h-full flex items-center justify-center p-1">
                    <video
                      v-if="index === currentSlideIndex && popup?.media_url && popup?.media_url.length > 0 && getMediaType(currentImageUrl) === 'video'"
                      :src="index === currentSlideIndex ? currentImageUrl : ''"
                      :key="`video-${index === currentSlideIndex ? currentPopup?.id : popup.id}-${currentImageIndex}`"
                      class="w-full h-full max-w-[472px] max-h-[315px]
                             object-contain object-center bg-gray-50 rounded-t-3xl rounded-b-xl cursor-pointer"
                      :autoplay="true"
                      playsinline
                      webkit-playsinline
                      controls
                      controlslist="nofullscreen noplaybackrate nodownload noremoteplaybook"
                      disablepictureinpicture
                      disableremoteplayback
                      @loadeddata="onVideoLoaded"
                      @canplay="onVideoCanPlay"
                      @volumechange="onVolumeChange"
                      @playing="onVideoPlaying"
                      @ended="() => nextImage(() => nextSlide())"
                      @seeking="onVideoSeeking"
                      @seeked="onVideoSeeked"
                      ref="videoElement"
                    />
                    <div
                      v-else-if="index === currentSlideIndex && popup?.media_url && popup?.media_url.length > 0 && getMediaType(currentImageUrl) === 'image'"
                      class="relative w-full h-full max-w-[472px] max-h-[315px]
                             rounded-t-3xl rounded-b-xl overflow-hidden bg-gray-50"
                    >
                      <NuxtImg
                        :src="index === currentSlideIndex ? currentImageUrl : ''"
                        :width="40"
                        :quality="20"
                        format="webp"
                        aria-hidden="true"
                        class="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
                        loading="lazy"
                        decoding="async"
                      />
                      <NuxtImg
                        :src="index === currentSlideIndex ? currentImageUrl : ''"
                        :alt="popup?.title"
                        class="relative w-full h-full object-contain object-center"
                        format="webp"
                        :quality="75"
                        :placeholder="false"
                        :preload="index === 0 && currentSlideIndex === 0"
                        :loading="index === 0 && currentSlideIndex === 0 ? 'eager' : 'lazy'"
                        :fetchpriority="index === 0 && currentSlideIndex === 0 ? 'high' : 'auto'"
                        decoding="async"
                        sizes="(max-width: 640px) 100vw, 472px"
                      />
                    </div>
                    <div v-else class="w-full h-full max-w-[472px] max-h-[315px]
                                       flex items-center justify-center
                                       bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-2xl rounded-b-xl">
                      <div class="text-center text-gray-400 p-4 sm:p-6">
                        <ImagePlaceholderIcon class="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                        <p class="font-medium text-xs sm:text-sm">Görsel Mevcut Değil</p>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

              <div class="flex flex-col min-h-0 flex-1">
                <div class="w-full max-w-[472px] mx-auto px-1 pt-3 flex-shrink-0">
                  <div class="flex items-center gap-1.5">
                    <button
                        v-for="(p, i) in popups"
                        :key="p.id"
                        @click="goToSlide(i)"
                        class="flex-1 h-1 rounded-full bg-gray-200/80 hover:bg-gray-300 relative overflow-hidden cursor-pointer transition-colors duration-200"
                        :aria-label="i === currentSlideIndex ? 'Aktif slayt' : (i + 1) + '. slayta git'"
                    >
                      <div
                        v-if="i === currentSlideIndex"
                        class="absolute inset-y-0 left-0 bg-gray-800 rounded-full"
                        :style="{ width: `${Math.min(100, (progress / currentSlideDurationMs) * 100)}%` }"
                      ></div>
                      <div
                        v-else-if="i < currentSlideIndex"
                        class="absolute inset-0 bg-gray-400 rounded-full"
                      ></div>
                    </button>
                  </div>
                </div>

                <div class="flex flex-col flex-1 min-h-0 justify-between overflow-hidden">
                  <div class="px-3 sm:px-5 pt-2 sm:pt-3 overflow-hidden">
                    <Transition
                      enter-active-class="transition-opacity duration-400 ease-in-out"
                      enter-from-class="opacity-0"
                      enter-to-class="opacity-100"
                      leave-active-class="transition-opacity duration-400 ease-in-out"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0"
                      mode="out-in"
                    >
                      <div :key="popup?.id" class="transition-delay-100 overflow-hidden">
                        <h2 class="text-[18px] sm:text-[20px] font-bold text-gray-900 mb-2 sm:mb-3
                                   pr-6 sm:pr-8 leading-[100%] tracking-[0%] font-sans break-words
                                   min-h-[18px] sm:min-h-[24px] truncate">
                          {{ popup?.title }}
                        </h2>

                        <p class="text-[16px] font-normal text-black
                                   leading-[120%] tracking-[0%] font-sans break-words overflow-hidden"
                           :class="getShortContent(popup?.content || '').length <= 50 ? 'mb-2 sm:mb-3' : 'mb-4 sm:mb-5'">
                          {{ getShortContent(popup?.content || '') }}
                        </p>
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>

              <div class="bg-white p-3 sm:p-4 md:pb-6 md:px-7 flex-shrink-0">
                <div class="flex items-center justify-between gap-2 sm:gap-4">
                  <div class="flex-1">
                    <NuxtLink
                      v-if="popup?.link"
                      :href="popup?.link"
                      target="_blank"
                      external
                      @click="incrementClickCount"
                      class="inline-flex items-center px-[16px] py-[14px] h-[45px]
                             bg-[#F4F4F4] text-gray-800 font-medium text-[14px]
                             leading-[100%] tracking-[0%] font-sans text-center
                             rounded-[100px] border border-gray-200 hover:bg-[#EFEFEF]
                             transition-all duration-300 flex-shrink-0"
                    >
                      {{ popup?.link_text + " →" || 'İncele →' }}
                    </NuxtLink>
                  </div>
                  <div v-if="popups.length > 1" class="flex items-center space-x-[16px] flex-shrink-0">
                    <button
                      @click="goToSlide(currentSlideIndex - 1 < 0 ? popups.length - 1 : currentSlideIndex - 1)"
                      class="w-[44px] h-[44px] rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-200
                             flex items-center justify-center transition-all duration-200"
                      aria-label="Önceki"
                    >
                      <ChevronLeftIcon class="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      @click="goToSlide((currentSlideIndex + 1) % popups.length)"
                      class="w-[44px] h-[44px] rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-200
                             flex items-center justify-center transition-all duration-200"
                      aria-label="Sonraki"
                    >
                      <ChevronRightIcon class="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>